import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import {
  Crop,
  GalleryHorizontalEnd,
  Grid3x3,
  Image,
  Layers,
  Layout,
  LayoutGrid,
  Palette,
  Type,
  type LucideIcon,
} from 'lucide-react';
import type { AnyDoc, CarouselDoc, ID, Project, SplitterDoc, ToolId } from '@/types';
import { useStore, DOC_KEY, type DocKey, type InspectorTab } from '@/state/store';
import { cellsInOrder } from '@nextcollage/engine';

export interface StageProps {
  onRequestPhotos: (cellId?: string) => void;
}

export interface ToolTab {
  value: InspectorTab;
  label: string;
  icon: LucideIcon;
}

export interface ToolDef {
  id: ToolId;
  /** shown in the switcher and the chooser card */
  label: string;
  icon: LucideIcon;
  /** the page that boots straight into this tool */
  path: string;
  docKey: DocKey;
  /** the sr-only <h1> — a real per-page SEO signal */
  title: string;
  /** one line on the chooser card */
  blurb: string;
  /** what the left panel is called for this tool */
  libraryLabel: string;
  defaultTab: InspectorTab;
  tabs: ToolTab[];
  Stage: LazyExoticComponent<ComponentType<StageProps>>;
  /** the right panel body for each of this tool's tabs */
  panels: Partial<Record<InspectorTab, LazyExoticComponent<ComponentType>>>;
  /** the status-bar summary */
  status: (doc: AnyDoc) => string;
  /**
   * What tapping a photo in the library means for this tool. Touch has no
   * drag-and-drop, so this is the primary way photos get placed on a phone.
   */
  placeAsset: (assetId: ID) => void;
  /** the library footer's primary action, or null when the tool has none */
  libraryHint: string;
}

const CollageStage = lazy(() =>
  import('@/components/canvas/CollageStage').then((m) => ({ default: m.CollageStage })),
);
const SplitterStage = lazy(() =>
  import('@/components/splitter/SplitterStage').then((m) => ({ default: m.SplitterStage })),
);
const CarouselStage = lazy(() =>
  import('@/components/carousel/CarouselStage').then((m) => ({ default: m.CarouselStage })),
);

const LayoutPanel = lazy(() =>
  import('@/components/inspector/LayoutPanel').then((m) => ({ default: m.LayoutPanel })),
);
const ImagePanel = lazy(() =>
  import('@/components/inspector/ImagePanel').then((m) => ({ default: m.ImagePanel })),
);
const StylePanel = lazy(() =>
  import('@/components/inspector/StylePanel').then((m) => ({ default: m.StylePanel })),
);
const GridPanel = lazy(() =>
  import('@/components/inspector/splitter/GridPanel').then((m) => ({ default: m.GridPanel })),
);
const SourcePanel = lazy(() =>
  import('@/components/inspector/splitter/SourcePanel').then((m) => ({ default: m.SourcePanel })),
);
const SlidesPanel = lazy(() =>
  import('@/components/inspector/carousel/SlidesPanel').then((m) => ({ default: m.SlidesPanel })),
);
const LayersPanel = lazy(() =>
  import('@/components/inspector/carousel/LayersPanel').then((m) => ({ default: m.LayersPanel })),
);
const LayerStylePanel = lazy(() =>
  import('@/components/inspector/carousel/LayerStylePanel').then((m) => ({
    default: m.LayerStylePanel,
  })),
);

export const TOOLS: Record<ToolId, ToolDef> = {
  collage: {
    id: 'collage',
    label: 'Collage',
    icon: LayoutGrid,
    path: '/',
    docKey: DOC_KEY.collage,
    title: 'NextCollage — online photo collage maker',
    blurb: 'Arrange several photos into one picture on a grid you can merge and split.',
    libraryLabel: 'Photos',
    defaultTab: 'layout',
    tabs: [
      { value: 'layout', label: 'Layout', icon: Layout },
      { value: 'image', label: 'Photo', icon: Image },
      { value: 'style', label: 'Style', icon: Palette },
    ],
    Stage: CollageStage,
    panels: { layout: LayoutPanel, image: ImagePanel, style: StylePanel },
    status: (doc) => {
      const p = doc as Project;
      const filled = Object.values(p.slots).filter((s) => s.assetId).length;
      return `${p.template.cols}×${p.template.rows} · ${p.template.cells.length} cells · ${filled} filled`;
    },
    // Into the selected cell if there is one, otherwise the first empty cell.
    placeAsset: (assetId) => {
      const s = useStore.getState();
      const { slots, template } = s.project;
      const target =
        s.ui.selection.find((id) => slots[id]) ??
        cellsInOrder(template).find((c) => !slots[c.id]?.assetId)?.id;
      if (!target) {
        s.toast('Every cell is filled — select one to replace it', 'info');
        return;
      }
      s.assign(target, assetId);
    },
    libraryHint: 'Tap a photo to place it, or drag it onto a cell.',
  },

  splitter: {
    id: 'splitter',
    label: 'Grid Split',
    icon: Grid3x3,
    path: '/grid-splitter',
    docKey: DOC_KEY.splitter,
    title: 'Image grid splitter — cut one photo into an Instagram puzzle grid',
    blurb: 'Cut one photo into tiles that line back up into the whole picture on your profile.',
    libraryLabel: 'Source',
    defaultTab: 'grid',
    tabs: [
      { value: 'grid', label: 'Grid', icon: Grid3x3 },
      { value: 'source', label: 'Photo', icon: Crop },
    ],
    Stage: SplitterStage,
    panels: { grid: GridPanel, source: SourcePanel },
    status: (doc) => {
      const d = doc as SplitterDoc;
      return `${d.cols}×${d.rows} · ${d.cols * d.rows} tiles`;
    },
    // The splitter cuts exactly one picture, so a tap replaces the source.
    placeAsset: (assetId) => useStore.getState().setSource(assetId),
    libraryHint: 'Tap a photo to make it the one being split.',
  },

  carousel: {
    id: 'carousel',
    label: 'Carousel',
    icon: GalleryHorizontalEnd,
    path: '/carousel',
    docKey: DOC_KEY.carousel,
    title: 'Seamless carousel maker — App Store and Instagram screenshots that continue',
    blurb: 'Lay out one wide design and cut it into slides an element flows across.',
    libraryLabel: 'Photos',
    defaultTab: 'slides',
    tabs: [
      { value: 'slides', label: 'Slides', icon: GalleryHorizontalEnd },
      { value: 'layers', label: 'Layers', icon: Layers },
      { value: 'style', label: 'Style', icon: Type },
    ],
    Stage: CarouselStage,
    panels: { slides: SlidesPanel, layers: LayersPanel, style: LayerStylePanel },
    status: (doc) => {
      const d = doc as CarouselDoc;
      const n = d.layers.length;
      return `${d.slides} slides · ${d.slideW}×${d.slideH} · ${n} layer${n === 1 ? '' : 's'}`;
    },
    placeAsset: (assetId) => useStore.getState().addImageLayer(assetId),
    libraryHint: 'Tap a photo to drop it onto the strip.',
  },
};

export const TOOL_ORDER: ToolId[] = ['collage', 'splitter', 'carousel'];

/** Which tool a pathname belongs to — used by the back/forward listener. */
export function toolForPath(path: string): ToolId | null {
  const clean = path.replace(/\/$/, '') || '/';
  return TOOL_ORDER.find((id) => TOOLS[id].path === clean) ?? null;
}
